import React, { useState } from 'react';
import { UserCircle, LogOut, Settings, Menu, X, Sun, Moon, Heart, History, Search, CreditCard } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSearchLimits } from '../../hooks/useSearchLimits';

export const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const [showMobileMenu, setShowMobileMenu] = React.useState(false);
  const { remainingSearches, isLoading } = useSearchLimits();

  const isAdmin = user?.email?.endsWith('@confirmbets.com');

  const handleSignOut = async () => {
    await signOut();
    setShowUserMenu(false);
    setShowMobileMenu(false);
    navigate('/');
  };

  const closeAllMenus = () => {
    setShowUserMenu(false);
    setShowMobileMenu(false);
  };

  // Calculate display credits
  const displaySearches = !isLoading ? (
    user ? remainingSearches : // For logged in users, show actual credits
    sessionStorage.getItem('footyAI_sessionId') ? remainingSearches : // For users with session, show remaining credits
    6 // For new users, show initial 6 credits
  ) : null;

  return (
    <header className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-gray-800 dark:to-gray-900 text-white shadow-lg transition-colors relative z-50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo and Title */}
          <Link to="/" className="flex flex-col hover:opacity-90 transition-opacity group">
            <img 
              src="/logo.png" 
              alt="Banga.ai Logo" 
              className="h-16 w-64 object-contain group-hover:scale-105 transition-transform"
            />
          </Link>

          <div className="flex items-center gap-4">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-blue-700/50 dark:hover:bg-gray-700 transition-colors"
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5" />
              ) : (
                <Sun className="w-5 h-5" />
              )}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="p-2 rounded-lg hover:bg-blue-700/50 dark:hover:bg-gray-700 transition-colors md:hidden"
              aria-label="Toggle menu"
            >
              {showMobileMenu ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <nav>
              <ul className="flex space-x-6">
                <li>
                  <Link 
                    to="/" 
                    className={`hover:text-blue-100 transition-colors ${
                      location.pathname === '/' ? 'text-white font-semibold' : 'text-blue-100'
                    }`}
                  >
                    Home
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/why-banga" 
                    className={`hover:text-blue-100 transition-colors ${
                      location.pathname === '/why-banga' ? 'text-white font-semibold' : 'text-blue-100'
                    }`}
                  >
                    Why Banga
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/markets" 
                    className={`hover:text-blue-100 transition-colors ${
                      location.pathname === '/markets' ? 'text-white font-semibold' : 'text-blue-100'
                    }`}
                  >
                    Markets
                  </Link>
                </li>
                {isAdmin && (
                  <li>
                    <Link 
                      to="/admin" 
                      className={`hover:text-blue-100 transition-colors ${
                        location.pathname === '/admin' ? 'text-white font-semibold' : 'text-blue-100'
                      }`}
                    >
                      Admin
                    </Link>
                  </li>
                )}
              </ul>
            </nav>
            
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 p-2 rounded-lg hover:bg-blue-700/50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-blue-500 dark:bg-gray-600 flex items-center justify-center">
                    <span className="text-sm font-medium">
                      {user.email?.[0].toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm hidden lg:block">{user.email}</span>
                </button>

                {showUserMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-40"
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div className="absolute right-0 mt-2 w-48 rounded-lg bg-white dark:bg-gray-800 shadow-lg py-1 text-gray-700 dark:text-gray-200 z-50">
                      {/* Search Limits */}
                      <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                        <div className="flex items-center gap-2 text-sm">
                          <Search className="w-4 h-4 text-gray-400" />
                          <span>{displaySearches !== null ? `${displaySearches} searches left` : 'Loading...'}</span>
                        </div>
                      </div>

                      <Link
                        to="/billing"
                        className="w-full px-4 py-2 text-left flex items-center space-x-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <CreditCard className="w-4 h-4" />
                        <span>Billing & Credits</span>
                      </Link>

                      <Link
                        to="/liked-searches"
                        className="w-full px-4 py-2 text-left flex items-center space-x-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Heart className="w-4 h-4" />
                        <span>Liked Searches</span>
                      </Link>

                      <Link
                        to="/search-history"
                        className="w-full px-4 py-2 text-left flex items-center space-x-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <History className="w-4 h-4" />
                        <span>Search History</span>
                      </Link>

                      {isAdmin && (
                        <Link
                          to="/admin"
                          className="w-full px-4 py-2 text-left flex items-center space-x-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <Settings className="w-4 h-4" />
                          <span>Admin Dashboard</span>
                        </Link>
                      )}

                      <button
                        onClick={handleSignOut}
                        className="w-full px-4 py-2 text-left flex items-center space-x-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Link
                to="/signup"
                className="flex items-center space-x-2 px-6 py-2.5 rounded-lg bg-white dark:bg-gray-700 text-blue-600 dark:text-white hover:bg-blue-50 dark:hover:bg-gray-600 font-medium shadow-md hover:shadow-lg transition-all"
              >
                <UserCircle className="w-5 h-5" />
                <span>Get Started</span>
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="md:hidden mt-4 pb-2">
            <nav className="space-y-2">
              <Link
                to="/"
                onClick={closeAllMenus}
                className={`block px-4 py-2 rounded-lg ${
                  location.pathname === '/' 
                    ? 'bg-blue-700/50 dark:bg-gray-700 text-white' 
                    : 'text-blue-100 hover:bg-blue-700/50 dark:hover:bg-gray-700'
                }`}
              >
                Home
              </Link>
              <Link
                to="/why-banga"
                onClick={closeAllMenus}
                className={`block px-4 py-2 rounded-lg ${
                  location.pathname === '/why-banga'
                    ? 'bg-blue-700/50 dark:bg-gray-700 text-white'
                    : 'text-blue-100 hover:bg-blue-700/50 dark:hover:bg-gray-700'
                }`}
              >
                Why Banga
              </Link>
              <Link
                to="/markets"
                onClick={closeAllMenus}
                className={`block px-4 py-2 rounded-lg ${
                  location.pathname === '/markets'
                    ? 'bg-blue-700/50 dark:bg-gray-700 text-white'
                    : 'text-blue-100 hover:bg-blue-700/50 dark:hover:bg-gray-700'
                }`}
              >
                Markets
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={closeAllMenus}
                  className={`block px-4 py-2 rounded-lg ${
                    location.pathname === '/admin'
                      ? 'bg-blue-700/50 dark:bg-gray-700 text-white'
                      : 'text-blue-100 hover:bg-blue-700/50 dark:hover:bg-gray-700'
                  }`}
                >
                  Admin
                </Link>
              )}
              {user ? (
                <div className="border-t border-blue-500 dark:border-gray-600 mt-3 pt-3">
                  <div className="px-4 py-2 text-sm text-blue-100">
                    Signed in as: {user.email}
                  </div>
                  <div className="px-4 py-2 text-sm text-blue-100">
                    {displaySearches !== null ? `${displaySearches} searches left` : 'Loading...'}
                  </div>
                  <Link
                    to="/billing"
                    onClick={closeAllMenus}
                    className="block px-4 py-2 text-blue-100 hover:bg-blue-700/50 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Billing & Credits
                  </Link>
                  <Link
                    to="/liked-searches"
                    onClick={closeAllMenus}
                    className="block px-4 py-2 text-blue-100 hover:bg-blue-700/50 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Liked Searches
                  </Link>
                  <Link
                    to="/search-history"
                    onClick={closeAllMenus}
                    className="block px-4 py-2 text-blue-100 hover:bg-blue-700/50 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Search History
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      onClick={closeAllMenus}
                      className="block px-4 py-2 text-blue-100 hover:bg-blue-700/50 dark:hover:bg-gray-700 rounded-lg"
                    >
                      Admin Dashboard
                    </Link>
                  )}
                  <button
                    onClick={handleSignOut}
                    className="w-full text-left px-4 py-2 text-blue-100 hover:bg-blue-700/50 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <div className="mt-3 px-4">
                  <div className="text-sm text-blue-100 mb-2">
                    {displaySearches !== null ? `${displaySearches} searches left` : 'Loading...'}
                  </div>
                  <Link
                    to="/signup"
                    onClick={closeAllMenus}
                    className="flex items-center justify-center space-x-2 px-6 py-2.5 rounded-lg bg-white dark:bg-gray-700 text-blue-600 dark:text-white hover:bg-blue-50 dark:hover:bg-gray-600 font-medium shadow-md hover:shadow-lg transition-all w-full"
                  >
                    <UserCircle className="w-5 h-5" />
                    <span>Get Started</span>
                  </Link>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};